import React, { useState, useMemo } from 'react';
import {
    Container, Box, Typography, Grid, Card, Chip, TextField,
    List, ListItem, ListItemButton, ListItemText, ListItemIcon, Collapse,
    Divider, IconButton, Tooltip, Paper, InputAdornment, useMediaQuery,
    Button
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
    Code, Security, Speed, Api, ContentCopy, ExpandMore, ExpandLess,
    Search, MenuBook, Http, CheckCircle, Error as ErrorIcon,
    VpnKey, DataObject, Lock, RocketLaunch as Rocket
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import WebsiteLayout from '../../components/Website/WebsiteLayout';
import HeroSection from '../../components/Website/HeroSection';
import SEO from '../../components/SEO/SEO';

// ─── Method badge colors ───
const METHOD_COLORS = {
    GET: { bg: '#e3f2fd', color: '#1565c0', border: '#90caf9' },
    POST: { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
    PUT: { bg: '#fff3e0', color: '#e65100', border: '#ffcc80' },
    DELETE: { bg: '#ffebee', color: '#c62828', border: '#ef9a9a' },
    PATCH: { bg: '#f3e5f5', color: '#7b1fa2', border: '#ce93d8' }
};

const MethodBadge = ({ method }) => {
    const style = METHOD_COLORS[method] || METHOD_COLORS.GET;
    return (
        <Chip
            label={method}
            size="small"
            sx={{
                fontWeight: 700,
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                bgcolor: style.bg,
                color: style.color,
                border: `1px solid ${style.border}`,
                minWidth: 64,
                borderRadius: '6px'
            }}
        />
    );
};

// ─── Code block component ───
const CodeBlock = ({ children, language = 'json', title }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(children);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ position: 'relative', my: 2 }}>
            {title && (
                <Typography variant="caption" sx={{
                    display: 'block', mb: 0.5, fontWeight: 600, color: 'text.secondary',
                    textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem'
                }}>
                    {title}
                </Typography>
            )}
            <Box sx={{
                bgcolor: '#1e1e2e', borderRadius: 2, p: 2, overflow: 'auto',
                position: 'relative', border: '1px solid #313244'
            }}>
                <Tooltip title={copied ? 'Copied!' : 'Copy'}>
                    <IconButton
                        size="small"
                        onClick={handleCopy}
                        sx={{
                            position: 'absolute', top: 8, right: 8, color: '#a6adc8',
                            '&:hover': { color: '#cdd6f4', bgcolor: 'rgba(255,255,255,0.08)' }
                        }}
                    >
                        <ContentCopy sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
                <Typography component="pre" sx={{
                    fontFamily: '"JetBrains Mono", "Fira Code", "Consolas", monospace',
                    fontSize: '0.8rem', color: '#cdd6f4', m: 0,
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6
                }}>
                    {children}
                </Typography>
            </Box>
        </Box>
    );
};

// ─── Endpoint Component ───
const EndpointEntry = ({ endpoint }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <Paper
            elevation={0}
            sx={{
                mb: 2, border: '1px solid', borderColor: 'divider',
                borderRadius: 2, overflow: 'hidden',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: 'primary.light', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }
            }}
        >
            <Box
                onClick={() => setExpanded(!expanded)}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 2,
                    px: 3, py: 2, cursor: 'pointer',
                    bgcolor: expanded ? 'grey.50' : 'transparent',
                    '&:hover': { bgcolor: 'grey.50' }
                }}
            >
                <MethodBadge method={endpoint.method} />
                <Typography sx={{
                    fontFamily: 'monospace', fontWeight: 600, fontSize: '0.875rem',
                    color: 'text.primary', flex: 1
                }}>
                    {endpoint.path}
                </Typography>
                <Typography variant="body2" sx={{
                    color: 'text.secondary', display: { xs: 'none', md: 'block' },
                    maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}>
                    {endpoint.description}
                </Typography>
                {expanded ? <ExpandLess color="action" /> : <ExpandMore color="action" />}
            </Box>

            <Collapse in={expanded}>
                <Divider />
                <Box sx={{ px: 3, py: 3 }}>
                    <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.7 }}>
                        {endpoint.description}
                    </Typography>

                    {/* Headers */}
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
                        Required Headers
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                        When generating credentials, you receive an <strong>API Key</strong> (starts with <code>sk_live_</code>) and an <strong>API Token</strong> (starts with <code>tok_</code>). Pass the API Token in the Authorization header and the API Key in the X-API-KEY header.
                    </Typography>
                    <CodeBlock title="Headers">{`Authorization: Bearer <API_TOKEN>\nX-API-KEY: <API_KEY>${endpoint.extraHeaders ? '\n' + endpoint.extraHeaders : ''}`}</CodeBlock>

                    {/* Parameters */}
                    {endpoint.pathParams && (
                        <>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 3, mb: 1, color: 'text.primary' }}>
                                Path Parameters
                            </Typography>
                            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', mb: 2 }}>
                                <Box component="thead">
                                    <Box component="tr" sx={{ bgcolor: 'grey.50' }}>
                                        <Box component="th" sx={{ p: 1.5, textAlign: 'left', fontWeight: 600, fontSize: '0.8rem', borderBottom: '1px solid', borderColor: 'divider' }}>Parameter</Box>
                                        <Box component="th" sx={{ p: 1.5, textAlign: 'left', fontWeight: 600, fontSize: '0.8rem', borderBottom: '1px solid', borderColor: 'divider' }}>Type</Box>
                                        <Box component="th" sx={{ p: 1.5, textAlign: 'left', fontWeight: 600, fontSize: '0.8rem', borderBottom: '1px solid', borderColor: 'divider' }}>Description</Box>
                                    </Box>
                                </Box>
                                <Box component="tbody">
                                    {endpoint.pathParams.map((p, i) => (
                                        <Box component="tr" key={i}>
                                            <Box component="td" sx={{ p: 1.5, fontFamily: 'monospace', fontSize: '0.8rem', borderBottom: '1px solid', borderColor: 'divider' }}>{p.name}</Box>
                                            <Box component="td" sx={{ p: 1.5, fontSize: '0.8rem', borderBottom: '1px solid', borderColor: 'divider' }}>{p.type}</Box>
                                            <Box component="td" sx={{ p: 1.5, fontSize: '0.8rem', borderBottom: '1px solid', borderColor: 'divider', color: 'text.secondary' }}>{p.desc}</Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </>
                    )}

                    {endpoint.queryParams && (
                        <>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 3, mb: 1, color: 'text.primary' }}>
                                Query Parameters
                            </Typography>
                            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', mb: 2 }}>
                                <Box component="thead">
                                    <Box component="tr" sx={{ bgcolor: 'grey.50' }}>
                                        <Box component="th" sx={{ p: 1.5, textAlign: 'left', fontWeight: 600, fontSize: '0.8rem', borderBottom: '1px solid', borderColor: 'divider' }}>Parameter</Box>
                                        <Box component="th" sx={{ p: 1.5, textAlign: 'left', fontWeight: 600, fontSize: '0.8rem', borderBottom: '1px solid', borderColor: 'divider' }}>Type</Box>
                                        <Box component="th" sx={{ p: 1.5, textAlign: 'left', fontWeight: 600, fontSize: '0.8rem', borderBottom: '1px solid', borderColor: 'divider' }}>Required</Box>
                                        <Box component="th" sx={{ p: 1.5, textAlign: 'left', fontWeight: 600, fontSize: '0.8rem', borderBottom: '1px solid', borderColor: 'divider' }}>Description</Box>
                                    </Box>
                                </Box>
                                <Box component="tbody">
                                    {endpoint.queryParams.map((p, i) => (
                                        <Box component="tr" key={i}>
                                            <Box component="td" sx={{ p: 1.5, fontFamily: 'monospace', fontSize: '0.8rem', borderBottom: '1px solid', borderColor: 'divider' }}>{p.name}</Box>
                                            <Box component="td" sx={{ p: 1.5, fontSize: '0.8rem', borderBottom: '1px solid', borderColor: 'divider' }}>{p.type}</Box>
                                            <Box component="td" sx={{ p: 1.5, fontSize: '0.8rem', borderBottom: '1px solid', borderColor: 'divider' }}>{p.required ? 'Yes' : 'No'}</Box>
                                            <Box component="td" sx={{ p: 1.5, fontSize: '0.8rem', borderBottom: '1px solid', borderColor: 'divider', color: 'text.secondary' }}>{p.desc}</Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </>
                    )}

                    {/* Request Body */}
                    {endpoint.requestBody && (
                        <CodeBlock title="Request Body">{endpoint.requestBody}</CodeBlock>
                    )}

                    {/* Success Response */}
                    {endpoint.successResponse && (
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'success.main', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                                    Success Response ({endpoint.successStatus || 200})
                                </Typography>
                            </Box>
                            <CodeBlock>{endpoint.successResponse}</CodeBlock>
                        </Box>
                    )}

                    {/* Error Response */}
                    {endpoint.errorResponse && (
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'error.main', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                                    Error Response
                                </Typography>
                            </Box>
                            <CodeBlock>{endpoint.errorResponse}</CodeBlock>
                        </Box>
                    )}
                </Box>
            </Collapse>
        </Paper>
    );
};

// ─── API Modules Data ───
const API_MODULES = [
    {
        id: 'authentication',
        name: 'Authentication',
        icon: <VpnKey />,
        description: 'Manage API credentials and verify access context',
        endpoints: [
            {
                method: 'GET',
                path: '/api/auth/verify',
                description: 'Verify current API credentials and retrieve tenant/company context',
                successResponse: `{
  "success": true,
  "status": "success",
  "data": {
    "tenantId": "tn_abc123",
    "companyId": "cmp_xyz789",
    "accessLevel": "full"
  }
}`,
                errorResponse: `{
  "success": false,
  "status": "error",
  "message": "Invalid API credentials. Access denied.",
  "code": "INVALID_CREDENTIALS"
}`
            }
        ]
    },
    {
        id: 'accounting',
        name: 'Accounting & GL',
        icon: <DataObject />,
        description: 'Manage chart of accounts, journal entries, and financial reconciliation',
        endpoints: [
            {
                method: 'GET',
                path: '/api/chart-of-accounts',
                description: 'List all accounts with current balances and parent hierarchy',
                queryParams: [
                    { name: 'type', type: 'string', required: false, desc: 'asset, liability, equity, revenue, expense' },
                    { name: 'isActive', type: 'boolean', required: false, desc: 'Filter by active status' }
                ],
                successResponse: `{ "success": true, "accounts": [{ "id": "acc_001", "name": "Cash", "code": "1001", "balance": 45000.50, ... }] }`
            },
            {
                method: 'POST',
                path: '/api/journal-entries',
                description: 'Create balanced journal entries with multi-currency support',
                requestBody: `{
  "date": "2024-03-26",
  "description": "Office Supplies",
  "lines": [
    { "accountId": "acc_exp_01", "debit": 250, "credit": 0, "notes": "Purchased paper" },
    { "accountId": "acc_cash_01", "debit": 0, "credit": 250, "notes": "Paid by cash" }
  ]
}`,
                successResponse: `{ "success": true, "entry": { "id": "je_987", "reference": "JE/2024/005" } }`
            }
        ]
    },
    {
        id: 'sales',
        name: 'Sales & Invoicing',
        icon: <DataObject />,
        description: 'Manage customers, quotations, and sales invoices',
        endpoints: [
            {
                method: 'GET',
                path: '/api/customers',
                description: 'Retrieve list of customers with contact and balance info',
                successResponse: `{ "success": true, "customers": [{ "id": "cust_123", "name": "Acme Corp", "email": "billing@acme.com", ... }] }`
            },
            {
                method: 'POST',
                path: '/api/invoices',
                description: 'Create sales invoice and trigger automated GL posting',
                requestBody: `{
  "customerId": "cust_123",
  "date": "2024-03-26",
  "items": [
    { "inventoryItemId": "itm_456", "quantity": 2, "unitPrice": 100 }
  ]
}`,
                successResponse: `{ "success": true, "invoice": { "id": "inv_001", "invoiceNumber": "INV-1001", "total": 200 } }`
            },
            {
                method: 'POST',
                path: '/api/payments',
                description: 'Record customer payment and reconcile against invoice(s)',
                requestBody: `{
  "customerId": "cust_123",
  "amount": 200,
  "date": "2024-03-27",
  "paymentMethod": "bank_transfer",
  "invoiceIds": ["inv_001"]
}`,
                successResponse: `{ "success": true, "payment": { "id": "pay_555", "reference": "RCPT-1001" } }`
            }
        ]
    },
    {
        id: 'purchasing',
        name: 'Purchasing & AP',
        icon: <DataObject />,
        description: 'Manage vendors, purchase orders, and supplier bills',
        endpoints: [
            {
                method: 'GET',
                path: '/api/vendors',
                description: 'Retrieve list of vendors/suppliers',
                successResponse: `{ "success": true, "vendors": [...] }`
            },
            {
                method: 'POST',
                path: '/api/purchase-orders',
                description: 'Create a new purchase order for stock replenishment',
                requestBody: `{ "vendorId": "vend_999", "items": [{ "inventoryItemId": "itm_789", "quantity": 100 }] }`,
                successResponse: `{ "success": true, "purchaseOrder": { "id": "po_777", "poNumber": "PO-2024-001" } }`
            }
        ]
    },
    {
        id: 'inventory',
        name: 'Inventory & Stock',
        icon: <DataObject />,
        description: 'Manage master items, warehouses, and stock movements',
        endpoints: [
            {
                method: 'GET',
                path: '/api/inventory/items',
                description: 'List products and services with real-time stock levels',
                successResponse: `{ "success": true, "items": [{ "sku": "PROD-01", "name": "Laptop", "stock": 45, "unit": "pcs", ... }] }`
            },
            {
                method: 'POST',
                path: '/api/inventory/stock/adjustment',
                description: 'Perform stock count adjustment or damage write-off',
                requestBody: `{ "inventoryItemId": "itm_456", "quantity": -2, "reason": "Damage", "warehouseId": "wh_main" }`,
                successResponse: `{ "success": true, "transactionId": "txn_888" }`
            }
        ]
    },
    {
        id: 'fiscal',
        name: 'Tax & Fiscal',
        icon: <DataObject />,
        description: 'Manage tax rates, fiscal periods, and period-end closing',
        endpoints: [
            {
                method: 'GET',
                path: '/api/tax-rates',
                description: 'List all configured tax rates and slabs',
                successResponse: `{ "success": true, "taxRates": [{ "id": "tax_vat_20", "name": "VAT 20%", "rate": 20.0, ... }] }`
            },
            {
                method: 'POST',
                path: '/api/fiscal/periods/close',
                description: 'Lock a fiscal period to prevent further backdated entries',
                requestBody: `{ "periodId": "fp_2024_03", "action": "close" }`,
                successResponse: `{ "success": true, "message": "Period locked successfully" }`
            }

        ]
    },
    {
        id: 'projects',
        name: 'Projects & Tasks',
        icon: <DataObject />,
        description: 'Manage projects, milestones, tasks, and timesheets',
        endpoints: [
            {
                method: 'GET',
                path: '/api/projects',
                description: 'Retrieve list of all active projects',
                successResponse: `{ "success": true, "projects": [{ "id": "proj_123", "name": "ERP Implementation", "status": "in_progress", ... }] }`
            },
            {
                method: 'POST',
                path: '/api/project-timesheets',
                description: 'Log time against a specific project or task',
                requestBody: `{ "projectId": "proj_123", "taskId": "task_456", "date": "2024-03-26", "hours": 4.5, "description": "Development work" }`,
                successResponse: `{ "success": true, "timesheet": { "id": "ts_789", "hours": 4.5 } }`
            }
        ]
    },
    {
        id: 'crm',
        name: 'CRM & Sales Pipeline',
        icon: <DataObject />,
        description: 'Manage leads, opportunities, and interactions',
        endpoints: [
            {
                method: 'GET',
                path: '/api/crm/leads',
                description: 'Retrieve list of sales leads',
                successResponse: `{ "success": true, "leads": [{ "id": "lead_001", "company": "Tech Corp", "status": "new", ... }] }`
            }
        ]
    }
];

// ─── Error codes data ───
const ERROR_CODES = [
    { code: '200', desc: 'Request succeeded', type: 'success' },
    { code: '201', desc: 'Resource created successfully', type: 'success' },
    { code: '400', desc: 'Bad request — invalid parameters', type: 'warning' },
    { code: '401', desc: 'Unauthorized — missing or invalid credentials', type: 'error' },
    { code: '403', desc: 'Forbidden — insufficient permissions', type: 'error' },
    { code: '404', desc: 'Resource not found', type: 'warning' },
    { code: '409', desc: 'Conflict — resource already exists', type: 'warning' },
    { code: '429', desc: 'Too many requests — rate limit exceeded', type: 'error' },
    { code: '500', desc: 'Internal server error', type: 'error' }
];

// ─── Main Documentation Page ───
const ApiDocumentationPage = () => {
    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.up('md'));
    const [activeModule, setActiveModule] = useState('authentication');
    const [searchTerm, setSearchTerm] = useState('');

    const filteredModules = useMemo(() => {
        if (!searchTerm) return API_MODULES;
        const term = searchTerm.toLowerCase();
        return API_MODULES.filter(m =>
            m.name.toLowerCase().includes(term) ||
            m.endpoints.some(e =>
                e.path.toLowerCase().includes(term) ||
                e.description.toLowerCase().includes(term)
            )
        ).map(m => ({
            ...m,
            endpoints: m.endpoints.filter(e =>
                e.path.toLowerCase().includes(term) ||
                e.description.toLowerCase().includes(term) ||
                m.name.toLowerCase().includes(term)
            )
        }));
    }, [searchTerm]);

    const activeModuleData = filteredModules.find(m => m.id === activeModule) || filteredModules[0];

    return (
        <WebsiteLayout>
            <SEO
                title="API Documentation - FluxERP"
                description="Complete API documentation for FluxERP. Integrate our accounting, invoicing, inventory, and business management APIs into your systems."
            />

            {/* Hero Section */}
            <HeroSection chip="API Documentation" py={{ xs: 6, md: 10 }}>
                <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
                    <Api sx={{ fontSize: 72, mb: 2, color: 'primary.contrastText' }} />
                    <Typography variant="h1" sx={{ fontSize: { xs: 32, md: 48 }, fontWeight: 800, mb: 2, color: 'primary.contrastText', letterSpacing: '-0.02em' }}>
                        API Documentation
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9, color: 'primary.contrastText', fontWeight: 400, mb: 4 }}>
                        Integrate FluxERP with your applications using our comprehensive REST API.
                        Secure, rate-limited, and designed for developers.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {[
                            { icon: <Security sx={{ fontSize: 20 }} />, label: 'API Key + Token Auth' },
                            { icon: <Speed sx={{ fontSize: 20 }} />, label: 'Rate Limited' },
                            { icon: <Code sx={{ fontSize: 20 }} />, label: 'RESTful JSON' }
                        ].map((badge, i) => (
                            <Chip key={i} icon={badge.icon} label={badge.label}
                                sx={{
                                    bgcolor: 'rgba(22,51,0,0.15)', color: 'primary.contrastText', fontWeight: 600,
                                    fontSize: '0.8rem', border: '1px solid rgba(22,51,0,0.25)',
                                    backdropFilter: 'blur(4px)',
                                    '& .MuiChip-icon': { color: 'primary.contrastText' }
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            </HeroSection>

            {/* Global Info Cards */}
            <Container maxWidth="lg" sx={{ mt: -4, mb: 4, position: 'relative', zIndex: 1 }}>
                <Grid container spacing={3}>
                    {[
                        {
                            icon: <Lock sx={{ color: 'secondary.main' }} />,
                            title: 'Authentication',
                            desc: 'Every request requires API Key + Token headers'
                        },
                        {
                            icon: <Http sx={{ color: 'success.main' }} />,
                            title: 'Base URL',
                            desc: 'https://fluxerp.co.uk/api'
                        },
                        {
                            icon: <Speed sx={{ color: 'warning.main' }} />,
                            title: 'Rate Limit',
                            desc: '60 requests/minute per company (configurable)'
                        }
                    ].map((card, i) => (
                        <Grid item xs={12} md={4} key={i}>
                            <Card sx={{
                                p: 3, height: '100%', display: 'flex', alignItems: 'flex-start', gap: 2,
                                border: '1px solid',
                                borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.200',
                                bgcolor: theme => theme.palette.mode === 'dark' ? 'background.paper' : 'white',
                                boxShadow: 'none'
                            }}>
                                <Box sx={{
                                    width: 48, height: 48, borderRadius: 2,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'grey.50', flexShrink: 0
                                }}>
                                    {card.icon}
                                </Box>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>{card.title}</Typography>
                                    <Typography variant="body2" sx={{
                                        color: 'text.secondary',
                                        fontFamily: card.title === 'Base URL' ? 'monospace' : 'inherit'
                                    }}>
                                        {card.desc}
                                    </Typography>
                                </Box>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* Getting Started */}
            <Container maxWidth="lg" sx={{ py: 6 }}>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 4 }} id="getting-started">
                    Getting Started
                </Typography>

                <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{
                            p: 3, height: '100%',
                            border: '1px solid',
                            borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.200',
                            bgcolor: theme => theme.palette.mode === 'dark' ? 'background.paper' : 'white'
                        }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                                <Security sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                                Authentication
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2, lineHeight: 1.8 }}>
                                All API requests must include two headers for authentication:
                            </Typography>
                            <CodeBlock>{`Authorization: Bearer <YOUR_API_TOKEN>
X-API-KEY: <YOUR_API_SECRET_KEY>`}</CodeBlock>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
                                You can generate your API credentials from Settings → API Access in your dashboard.
                                Credentials are shown only once on generation — store them securely.
                            </Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card sx={{
                            p: 3, height: '100%',
                            border: '1px solid',
                            borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.200',
                            bgcolor: theme => theme.palette.mode === 'dark' ? 'background.paper' : 'white'
                        }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                                <MenuBook sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                                Quick Example
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2, lineHeight: 1.8 }}>
                                Here's a quick example using cURL to list your customers:
                            </Typography>
                            <CodeBlock language="bash">{`curl -X GET https://fluxerp.co.uk/api/customers \\
  -H "Authorization: Bearer tok_abc123..." \\
  -H "X-API-KEY: sk_live_xyz789..." \\
  -H "Content-Type: application/json"`}</CodeBlock>
                        </Card>
                    </Grid>
                </Grid>

                {/* Error Handling */}
                <Box sx={{ mt: 6 }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 3 }} id="error-handling">
                        Error Handling
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.8 }}>
                        The API uses standard HTTP status codes to indicate success or failure. All error responses include
                        a JSON body with <code>status</code>, <code>message</code>, and optional <code>code</code> fields.
                    </Typography>
                    <Card sx={{
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'divider',
                        bgcolor: theme => theme.palette.mode === 'dark' ? 'background.paper' : 'white'
                    }}>
                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                            <Box component="thead">
                                <Box component="tr" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'grey.50' }}>
                                    <Box component="th" sx={{ p: 2, textAlign: 'left', fontWeight: 700, fontSize: '0.85rem' }}>Status Code</Box>
                                    <Box component="th" sx={{ p: 2, textAlign: 'left', fontWeight: 700, fontSize: '0.85rem' }}>Description</Box>
                                </Box>
                            </Box>
                            <Box component="tbody">
                                {ERROR_CODES.map((e, i) => (
                                    <Box component="tr" key={i} sx={{ '&:hover': { bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'grey.50' } }}>
                                        <Box component="td" sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                            <Chip
                                                label={e.code}
                                                size="small"
                                                sx={{
                                                    fontWeight: 700, fontFamily: 'monospace',
                                                    bgcolor: e.type === 'success' ? 'success.light' : e.type === 'warning' ? 'warning.light' : 'error.light',
                                                    color: 'white'
                                                }}
                                            />
                                        </Box>
                                        <Box component="td" sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', fontSize: '0.875rem', color: 'text.secondary' }}>
                                            {e.desc}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Card>
                </Box>

                {/* Rate Limiting */}
                <Box sx={{ mt: 6 }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 3 }} id="rate-limits">
                        Rate Limiting
                    </Typography>
                    <Card sx={{
                        p: 3,
                        border: '1px solid',
                        borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.200',
                        bgcolor: theme => theme.palette.mode === 'dark' ? 'background.paper' : 'white'
                    }}>
                        <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 2 }}>
                            API requests are rate-limited per company/tenant to ensure fair usage. The default limit is
                            <strong> 60 requests per minute</strong>. Rate limit information is included in response headers:
                        </Typography>
                        <CodeBlock>{`RateLimit-Limit: 60
RateLimit-Remaining: 45
RateLimit-Reset: 1708000000`}</CodeBlock>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
                            If you exceed the rate limit, you'll receive a <code>429 Too Many Requests</code> response.
                            Wait until the reset time before making additional requests.
                        </Typography>
                    </Card>
                </Box>

                {/* Versioning */}
                <Box sx={{ mt: 6 }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 3 }} id="versioning">
                        Versioning
                    </Typography>
                    <Card sx={{
                        p: 3,
                        border: '1px solid',
                        borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.200',
                        bgcolor: theme => theme.palette.mode === 'dark' ? 'background.paper' : 'white'
                    }}>
                        <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                            The API uses URL-based versioning. The current version is <strong>v1</strong>. All endpoints
                            are prefixed with <code>/api/</code>. We will announce major version changes in advance and
                            maintain backward compatibility within a version.
                        </Typography>
                    </Card>
                </Box>
            </Container>

            {/* API Reference */}
            <Box sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'background.default' : 'grey.50', py: 6 }} id="api-reference">
                <Container maxWidth="lg">
                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 4 }}>
                        API Reference
                    </Typography>

                    <Grid container spacing={3}>
                        {/* Sidebar */}
                        <Grid item xs={12} md={3}>
                            <Card sx={{
                                p: 2, position: isMd ? 'sticky' : 'static', top: 24,
                                border: '1px solid',
                                borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.200',
                                bgcolor: theme => theme.palette.mode === 'dark' ? 'background.paper' : 'white'
                            }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Search APIs..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search sx={{ fontSize: 20, color: 'text.secondary' }} />
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{ mb: 2 }}
                                />
                                <List dense sx={{ p: 0 }}>
                                    {filteredModules.map((module) => (
                                        <ListItem key={module.id} disablePadding>
                                            <ListItemButton
                                                selected={activeModule === module.id}
                                                onClick={() => setActiveModule(module.id)}
                                                sx={{
                                                    borderRadius: 1.5, mb: 0.5,
                                                    '&.Mui-selected': {
                                                        bgcolor: 'primary.main', color: 'secondary.main',
                                                        '&:hover': { bgcolor: '#8FD760' },
                                                        '& .MuiListItemIcon-root': { color: 'secondary.main' }
                                                    }
                                                }}
                                            >
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    {module.icon}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={module.name}
                                                    primaryTypographyProps={{
                                                        fontWeight: 600,
                                                        fontSize: '0.85rem',
                                                        color: 'inherit'
                                                    }}
                                                />
                                                <Chip
                                                    label={module.endpoints.length}
                                                    size="small"
                                                    sx={{
                                                        height: 20, fontSize: '0.7rem', fontWeight: 700,
                                                        bgcolor: activeModule === module.id ? 'rgba(255,255,255,0.2)' : (theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'grey.100')
                                                    }}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            </Card>
                        </Grid>

                        {/* Content */}
                        <Grid item xs={12} md={9}>
                            {activeModuleData && (
                                <Box>
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                                            {activeModuleData.name}
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                            {activeModuleData.description}
                                        </Typography>
                                    </Box>

                                    {activeModuleData.endpoints.map((endpoint, idx) => (
                                        <EndpointEntry key={idx} endpoint={endpoint} />
                                    ))}
                                </Box>
                            )}

                            {filteredModules.length === 0 && (
                                <Card sx={{
                                    p: 6, textAlign: 'center',
                                    border: '1px solid',
                                    borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.200',
                                    bgcolor: theme => theme.palette.mode === 'dark' ? 'background.paper' : 'white'
                                }}>
                                    <Search sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                                    <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                                        No APIs match your search
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        Try a different search term
                                    </Typography>
                                </Card>
                            )}
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* CTA Section - Matching AboutPage Security Section Style */}
            <Box
                sx={{
                    bgcolor: 'primary.main',
                    color: 'secondary.main',
                    py: 12,
                    mb: 0,
                    textAlign: 'center'
                }}
            >
                <Container maxWidth="md">
                    <Box sx={{ textAlign: 'center' }}>
                        <Rocket sx={{ fontSize: 64, mb: 3, color: 'secondary.main' }} />
                        <Typography variant="h2" sx={{ fontSize: { xs: 36, md: 48 }, fontWeight: 800, mb: 3, color: 'secondary.main' }}>
                            Ready to build with FluxERP?
                        </Typography>
                        <Typography variant="h6" sx={{ opacity: 0.95, lineHeight: 1.8, color: 'secondary.main', mb: 4 }}>
                            Sign up for a plan with API access enabled, generate your credentials,
                            and start building powerful integrations today. Our REST API is designed
                            to be developer-friendly, secure, and highly scalable.
                        </Typography>
                        <Button
                            variant="contained"
                            component={Link}
                            to="/pricing"
                            sx={{
                                bgcolor: 'secondary.main',
                                color: 'white',
                                fontWeight: 700,
                                px: 4,
                                py: 2,
                                borderRadius: 2,
                                transition: 'all 0.3s',
                                '&:hover': {
                                    bgcolor: 'secondary.dark',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 24px rgba(22, 51, 0, 0.25)'
                                }
                            }}
                        >
                            Explore Plans
                        </Button>
                    </Box>
                </Container>
            </Box>
        </WebsiteLayout>
    );
};

export default ApiDocumentationPage;
